import { Link, navigate } from 'gatsby';
import React, { useEffect, useRef, useState } from 'react';
import Section, { size } from '../Section';

import { isBuildTargetWebex } from '../../../utils/getBuildTarget';
import { mergeClassNames } from '../../../utils';
import styles from './index.module.scss';

export default function Breadcrumbs({ children, asGrid = false, neverHide = false, className = '' }) {
  let [isClosed, set] = useState(false);
  useTriggerOnDirectionalMovement(100, set);
  if (!children) return null;
  return (
    <Section
      as="nav"
      size={size.max}
      className={mergeClassNames(
        styles.breadcrumbs,
        asGrid ? styles.grid : styles.default,
        isClosed && !neverHide ? styles.hidden : '',
        isBuildTargetWebex() ? styles.webex : '',
        className
      )}
      innerClassName={styles.inner}
    >
      <ul>{children}</ul>
    </Section>
  );
}

export function LocationBreadcrumbs({ children }) {
  return (
    <li className={mergeClassNames(styles.breadcrumb, styles.location)}>
      <ul>{children}</ul>
    </li>
  );
}

export function BreadcrumbLink({ to, children }) {
  return (
    <li className={mergeClassNames(styles.breadcrumb, styles.link)}>
      <Link to={to} activeClassName={styles.active}>
        {children}
      </Link>
    </li>
  );
}

export function BreadcrumbGoToAnchor({ id, children }) {
  return (
    <li className={mergeClassNames(styles.breadcrumb, styles.button)}>
      <button onClick={() => navigate(`#${id}`, { state: { anchor: true } })}>{children}</button>
    </li>
  );
}

/**
 * trigger a callback after moved a distance of {distanceTriggerThreshold} px length
 * downward callback is called with `true`, upwards callback is called with `false`
 * @param {number} distanceTriggerThreshold distanceInPixels before callback()
 * @param {(setForward: ((forward: boolean) => boolean)) => void} callback
 */
function useTriggerOnDirectionalMovement(distanceTriggerThreshold = 100, callback) {
  let prevMove = useRef(null);
  let prevDirection = useRef(null);
  let prevDirectionChangePos = useRef(null);
  useEffect(() => {
    /** @param {Event} event */
    function onScroll(event) {
      let { current: lastDirection } = prevDirection; // let { current: lastMove } = prevMove;
      let { current: lastDirectionChangePos } = prevDirectionChangePos;
      let direction = window.scrollY - prevMove.current;
      let directionChange = signNumber(direction) !== signNumber(lastDirection);
      if (directionChange) prevDirectionChangePos.current = window.scrollY;
      let distanceMoved = lastDirectionChangePos - window.scrollY;
      callback(curIsOpen => {
        switch (true) {
          case distanceMoved < -distanceTriggerThreshold:
            return true;
          case distanceMoved > distanceTriggerThreshold:
            return false;
          default:
            return curIsOpen;
        }
      });
      prevDirection.current = direction;
      prevMove.current = window.scrollY;
      return void event;
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  });
}

/** @param {number} x */
function signNumber(x) {
  /** @param {number} x */
  function sign(x) {
    // eslint-disable-next-line no-self-compare -- NaN check
    return (x = +x) === 0 || x !== x ? x : x < 0 ? -1 : 1; // extracted from core-js polyfill https://github.com/zloirock/core-js/blob/b6432fbb749c76d6d4ab0cb8dc2d4f9351a86470/packages/core-js/internals/math-sign.js#L6
  }
  if (typeof Math.sign !== 'function') return sign(x);
  return Math.sign(x);
}
